import { z } from "zod";
import { homePhaseSchema } from "./home.js";
/* The client-declaration write path: the only thing that may move a journey
 * past a point the platform has no record of (root CLAUDE.md's "the software
 * does not interpret", journey/stage.ts). `day0_intent` is the webstore
 * ladder's fork — a genuine choice between two answers.
 */
export const declarationKindSchema = z.enum(["day0_intent"]);
export const day0IntentChoiceSchema = z.enum(["yes", "no"]);
// Strict on write, per the chat.ts / labs.ts split — an unknown kind or
// choice is a 400, not silently accepted.
export const declarationRequestSchema = z.object({
    kind: z.literal("day0_intent"),
    choice: day0IntentChoiceSchema,
});
/* The client putting a Home card off their own screen — a closed enum, not a
 * free string: an unknown id is a 400, same strictness as
 * declarationRequestSchema above.
 *
 * Neither id is rendered any more (home was stripped to notifications, and
 * the infusion steps moved to a tab with no dismiss). Left as-is rather than
 * replaced with a placeholder: the next dismissible landing-page block
 * replaces this list.
 */
export const dismissRequestSchema = z.object({
    block_id: z.enum(["bookDraw", "infusionSteps"]),
});
/* ---------------------------------------------------------------------------
 * The clinic's read of a customer's journey position.
 *
 * A staff-facing shape, deliberately separate from homeSurfaceSchema: the
 * client app is forbidden from branching on `stage` at all (see
 * journey/phase.ts's STAGE_TO_PHASE comment), while this pane's whole job is
 * to show it. Nothing here carries a measured value — the journey engine
 * cannot produce one, and journey-purity.test.ts enforces that upstream.
 * --------------------------------------------------------------------------- */
// Mirrors RungId (journey/spine.ts). A separate literal list, not an import,
// since api-contract must not depend on apps/api — the same convention
// homePhaseSchema follows for HOME_PHASES.
// Mirrors journey_tracks.track (@ferrivia/db). A separate literal list, not
// an import, for the same reason journeyRungIdSchema above is: api-contract
// must not depend on apps/api.
export const journeyTrackSchema = z.enum(["webstore", "infusion"]);
export const journeyRungIdSchema = z.enum([
    "awaiting_draw",
    "prereg",
    "result_ready",
    // The Day-0 fork is one rung holding two stages, not two rungs — see
    // spine.ts for why a rung each would misread a customer's own choice.
    "day0",
    "second_result",
]);
export const journeyRungStateSchema = z.enum(["past", "current", "future"]);
export const journeyRungSchema = z.object({
    id: journeyRungIdSchema,
    state: journeyRungStateSchema,
    /** YYYY-MM-DD, or null on a rung not yet reached. */
    reachedOn: z.string().nullable(),
    /** The phase this rung resolved to, which is what its copy is keyed by.
        Null where the branch is not yet known — the unanswered Day-0 fork. */
    phase: homePhaseSchema.nullable(),
});
export const journeyDeclarationSchema = z.object({
    kind: declarationKindSchema,
    choice: z.string(),
    declaredOn: z.string(),
});
export const clinicJourneySchema = z.object({
    stage: z.string(),
    phase: homePhaseSchema.catch("default"),
    rungs: z.array(journeyRungSchema),
    declarations: z.array(journeyDeclarationSchema),
});
